import { useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  acknowledgeHandover,
  getHandover,
  getShiftSummary,
  listHandovers,
  saveDraft,
  submitHandover,
} from './api'
import type {
  AcknowledgePayload,
  HandoverDetail,
  HandoverListFilters,
  SaveDraftPayload,
} from './types'

export const handoverKeys = {
  shift: ['handovers', 'shift-summary'] as const,
  list: (f: HandoverListFilters) => ['handovers', 'list', f] as const,
  detail: (id: string) => ['handovers', 'detail', id] as const,
}

export function useShiftSummary() {
  return useQuery({
    queryKey: handoverKeys.shift,
    queryFn: () => getShiftSummary(),
    staleTime: 30_000,
  })
}

export function useHandovers(filters: HandoverListFilters) {
  return useQuery({
    queryKey: handoverKeys.list(filters),
    queryFn: () => listHandovers(filters),
    staleTime: 30_000,
  })
}

export function useHandover(id: string | undefined) {
  return useQuery({
    queryKey: handoverKeys.detail(id ?? ''),
    queryFn: () => getHandover(id!),
    enabled: id !== undefined && id !== '',
    staleTime: 30_000,
  })
}

// Stable id so repeated autosave failures (e.g. during an outage) update
// the same toast in place instead of stacking a new one every 30s.
const DRAFT_SAVE_ERROR_TOAST_ID = 'handover-draft-save-error'

export function useSaveDraft() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (p: SaveDraftPayload) => saveDraft(p),
    onSuccess: ({ handover }) => {
      qc.setQueryData(handoverKeys.detail(handover.id), handover)
      void qc.invalidateQueries({ queryKey: handoverKeys.shift })
      toast.dismiss(DRAFT_SAVE_ERROR_TOAST_ID)
    },
    onError: (err) =>
      toast.error(
        err instanceof Error ? err.message : "Couldn't save draft",
        {
          id: DRAFT_SAVE_ERROR_TOAST_ID,
          description:
            'Your notes are still in the form — nothing is lost. Try Save draft again.',
        },
      ),
  })
}

export function useSubmitHandover() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => submitHandover({ id }),
    onSuccess: ({ handover }) => {
      qc.setQueryData(handoverKeys.detail(handover.id), handover)
      void qc.invalidateQueries({ queryKey: ['handovers'] })
      toast.success('Handover submitted to incoming shift')
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'Submit failed'),
  })
}

export function useAcknowledgeHandover() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (p: AcknowledgePayload) => acknowledgeHandover(p),
    onSuccess: ({ handover }) => {
      qc.setQueryData(handoverKeys.detail(handover.id), handover)
      void qc.invalidateQueries({ queryKey: ['handovers'] })
      toast.success('Acknowledged. Welcome to your shift.')
    },
  })
}

interface AutoSaveOpts {
  shift_id: string
  draft: HandoverDetail | null
  notes: string
  pending: string
  escalations: string
  attachments: HandoverDetail['attachments']
  enabled: boolean
  // Notified on every autosave attempt so the caller can drive a
  // persistent "autosave failed" indicator that survives the toast's
  // auto-dismiss, and clear it again once a save (autosave OR manual)
  // succeeds. Kept as caller-owned state rather than internal to this
  // hook so a manual "Save draft" success can also clear it.
  onAutosaveError?: () => void
  onAutosaveSuccess?: () => void
}

/**
 * AF-135 — autosave the draft every 30s when content has changed since
 * the last save. The save mutation is shared with the manual Save button.
 *
 * The interval ticks even when the page is hidden (analysts often Cmd-tab
 * while typing); we still want to capture their work, so we don't gate
 * on `document.visibilityState`.
 */
export function useAutosaveDraft(opts: AutoSaveOpts) {
  const save = useSaveDraft()
  const lastSavedRef = useRef<{ notes: string; pending: string; escalations: string }>(
    {
      notes: opts.draft?.handover_notes ?? '',
      pending: opts.draft?.pending_actions ?? '',
      escalations: opts.draft?.escalations ?? '',
    },
  )
  const { onAutosaveError, onAutosaveSuccess } = opts

  useEffect(() => {
    if (!opts.enabled) return
    const id = window.setInterval(() => {
      const cur = lastSavedRef.current
      if (
        cur.notes === opts.notes &&
        cur.pending === opts.pending &&
        cur.escalations === opts.escalations
      ) {
        return
      }
      save.mutate(
        {
          id: opts.draft?.id ?? null,
          shift_id: opts.shift_id,
          handover_notes: opts.notes,
          pending_actions: opts.pending,
          escalations: opts.escalations,
          attachments: opts.attachments,
        },
        {
          // Only lastSavedRef is updated on success — never on failure —
          // so a failed autosave is retried on the next tick with the
          // same (still-unsaved) content instead of being marked saved.
          onSuccess: () => {
            lastSavedRef.current = {
              notes: opts.notes,
              pending: opts.pending,
              escalations: opts.escalations,
            }
            onAutosaveSuccess?.()
          },
          onError: () => onAutosaveError?.(),
        },
      )
    }, 30_000)
    return () => window.clearInterval(id)
    // We deliberately re-run the interval setup whenever the latest
    // values change so the closure inside the timer always reads the
    // freshest text. The interval itself is cheap.
  }, [
    opts.enabled,
    opts.shift_id,
    opts.notes,
    opts.pending,
    opts.escalations,
    opts.attachments,
    opts.draft?.id,
    onAutosaveError,
    onAutosaveSuccess,
    save,
  ])

  return save
}
